import './theme/App.css';
import './theme/redesign.css';
import Header from '@/components/header/header';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { T } from '@/theme/tokens';
import { ActionCenterViewProvider } from '@/contexts/action-center-view-context';
import { AuthProvider } from '@/auth/AuthProvider';

// ============================================================================
// MUI theme aligned to the CloudCost.dc.html design language.
// Front-end only — restyles the shared MUI building blocks (buttons, toggles,
// chips, tabs, dialogs, inputs, tables) so the new look propagates everywhere.
// ============================================================================
const theme = createTheme({
  palette: {
    primary: { main: T.primary, dark: T.primaryHover, light: T.primaryLight },
    success: { main: T.success },
    warning: { main: T.warn },
    error: { main: T.danger },
    secondary: { main: T.ai },
    background: { default: T.bg, paper: T.surface },
    text: { primary: T.text, secondary: T.textSoft },
    divider: T.border,
  },
  shape: { borderRadius: 9 },
  typography: {
    fontFamily: T.font,
    h1: { fontWeight: 900, letterSpacing: '-0.5px', color: T.text },
    h2: { fontWeight: 900, letterSpacing: '-0.4px', color: T.text },
    h3: { fontWeight: 900, color: T.text },
    h4: { fontWeight: 900, color: T.text },
    h5: { fontWeight: 900, color: T.text },
    h6: { fontWeight: 900, color: T.text },
    subtitle1: { fontWeight: 700 },
    subtitle2: { fontWeight: 700 },
    button: { fontWeight: 700, textTransform: 'none' },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: T.bg,
          color: T.text,
          fontFamily: T.font,
          fontVariantNumeric: 'tabular-nums',
          WebkitFontSmoothing: 'antialiased',
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 700,
          borderRadius: T.radiusSm,
          boxShadow: 'none',
        },
        containedPrimary: { '&:hover': { backgroundColor: T.primaryHover } },
        outlined: { borderColor: T.borderInput },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          backgroundColor: T.neutralTint,
          border: `1px solid ${T.border}`,
          borderRadius: T.radiusSm,
          padding: 3,
          gap: 4,
        },
        grouped: {
          border: 'none !important',
          borderRadius: `${T.radiusXs}px !important`,
          margin: 0,
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 700,
          fontSize: 12.5,
          color: T.textFaint,
          border: 'none',
          padding: '6px 12px',
          lineHeight: 1.2,
          '&.Mui-selected': {
            backgroundColor: T.surface,
            color: T.text,
            boxShadow: '0 1px 2px rgba(16,35,58,.12)',
            '&:hover': { backgroundColor: T.surface },
          },
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.5)' },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 700, fontSize: 12, borderRadius: 999 },
        label: { paddingLeft: 10, paddingRight: 10 },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { height: 2.5, borderRadius: 2, backgroundColor: T.primary },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 700,
          fontSize: 14,
          color: T.textFaint,
          minHeight: 46,
          '&.Mui-selected': { color: T.primary },
        },
      },
    },
    MuiPaper: {
      styleOverrides: { rounded: { borderRadius: T.radius } },
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: T.radius } },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: T.radiusSm,
          backgroundColor: T.surface,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: T.borderInput },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: T.primaryLight },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          border: `1px solid ${T.border}`,
          borderRadius: `${T.radiusSm}px !important`,
          boxShadow: 'none',
          '&:before': { display: 'none' },
          overflow: 'hidden',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 700,
          fontSize: 11,
          letterSpacing: '.6px',
          textTransform: 'uppercase',
          color: T.textFaint3,
        },
        root: { borderColor: T.divider },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: T.radiusSm, fontWeight: 700, fontSize: 13, alignItems: 'center' },
        standardSuccess: { backgroundColor: T.successTint, color: T.success },
        standardError: { backgroundColor: T.dangerTint, color: T.danger },
        standardWarning: { backgroundColor: T.warnTint2, color: T.warnDark },
        standardInfo: { backgroundColor: T.primaryTint, color: T.primary },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: T.text,
          color: '#fff',
          fontSize: 12,
          fontWeight: 700,
          borderRadius: 8,
          padding: '7px 10px',
          boxShadow: T.popShadow,
        },
        arrow: { color: T.text },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: T.radiusSm,
          border: `1px solid ${T.border}`,
          boxShadow: T.popShadow,
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: { borderRadius: T.radiusSm, boxShadow: T.popShadow },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: { fontSize: 13.5, fontWeight: 600 },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: { borderRadius: T.radiusSm, boxShadow: T.popShadow },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: { color: T.primary, textDecorationColor: 'rgba(28,113,227,.4)', fontWeight: 700 },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: { '& .Mui-checked': { color: T.primary } },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 999, backgroundColor: T.divider },
        bar: { borderRadius: 999 },
      },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: T.divider } },
    },
  },
});

const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div className="App">
        <AuthProvider>
          <ActionCenterViewProvider>
            <Header />
          </ActionCenterViewProvider>
        </AuthProvider>
      </div>
    </ThemeProvider>
  );
};

export default App;
